/**
 * Shared punch pairing.
 *
 * Every screen and report that reports "hours worked" needs the same thing:
 * walk a chronological run of punches, pair each clock-in with its clock-out,
 * and sum the completed sessions. Before this module lived here, four call
 * sites did it independently and three of them disagreed — most visibly
 * apps/web punch-history, which took (last clock-out - first clock-in) and so
 * billed every off-clock gap on a multi-shift day as worked time.
 *
 * Grouping by day is NOT done here, but it is no longer left to the callers
 * either: `./day-keys` owns it. Leaving it to them was a mistake — three
 * different day keys emerged, and every caller but one bucketed raw punches
 * BEFORE pairing, which split an overnight shift into two unpaired halves and
 * reported it as zero hours. Callers now pair the whole range they fetched
 * and attribute the resulting sessions with `groupSessionsByDay`.
 *
 * Still deliberately NOT handled here, because the callers genuinely disagree
 * and unifying them would silently move reported hours:
 *
 *  - Rounding. api-time rounds each day to 2dp before splitting at 8h;
 *    api-payroll splits the unrounded value and rounds only period totals.
 *  - Filtering by status. api-time drops CORRECTED rows and queries the
 *    replacement separately; payroll's caller has already filtered them.
 *
 * Callers normalize into `PunchEvent` at their own boundary and keep those
 * decisions visible in their own code. The spelling of the action field is
 * NOT one of those decisions — see `./punch-actions`.
 */
/**
 * Order by string comparison on `at` — the default, because it is what the
 * timesheet, payroll and punch-history paths have always done.
 *
 * Careful: this is not true chronological order when punches carry different
 * precision. '09:00:00.500Z' sorts BEFORE '09:00:00Z' because '.' precedes
 * 'Z', even though it is the later instant. Changing the default would move
 * reported hours, so callers that need real chronology opt in to `byInstant`.
 */
export const byTimestampString = (a, b) => a.at.localeCompare(b.at);
/** Order by parsed instant — true chronological order. */
export const byInstant = (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime();
const MS_PER_HOUR = 1000 * 60 * 60;
/**
 * Pair a run of punches into completed work sessions.
 *
 * Events are sorted with `options.compare`, defaulting to string comparison
 * on `at` rather than parsed instant. That default matches what the timesheet,
 * payroll and punch-history paths already did and is load-bearing: punches are
 * written with inconsistent precision (kiosk punches carry no milliseconds,
 * web punches do), so switching them to epoch ordering would reorder
 * same-second punches and move reported hours. Pass `byInstant` for true
 * chronological order.
 *
 * The rules below are the ones api-time and api-payroll already implemented
 * identically:
 *
 *  - `clock_in` opens a session. If one is already open it is DISCARDED, not
 *    closed — an employee who never clocked out gets no time, rather than
 *    having the gap until their next shift counted.
 *  - `clock_out` closes the open session. With none open it is ignored.
 *  - `break_start` opens a break on the current session; outside a session it
 *    is ignored. `break_end` closes the most recent still-open break.
 *  - A trailing open session, and every break inside it, contributes nothing.
 *
 * Pass `options.maxSessionHours` to bound how far a `clock_out` may reach back
 * for its `clock_in`. Callers that pair a whole period at once should set it —
 * see that option for the 276-hour session that motivated it — and should pass
 * `options.onOverCap` with it, so what the cap removes is reported rather than
 * silently missing.
 */
export function pairWorkSessions(events, options = {}) {
    const ordered = [...events].sort(options.compare ?? byTimestampString);
    const maxSessionMs = options.maxSessionHours === undefined ? null : options.maxSessionHours * MS_PER_HOUR;
    const sessions = [];
    let open = null;
    for (const event of ordered) {
        switch (event.kind) {
            case 'clock_in': {
                // Drops any session that never clocked out.
                open = { clockIn: new Date(event.at), breaks: [] };
                break;
            }
            case 'clock_out': {
                if (!open)
                    break;
                const clockOut = new Date(event.at);
                // Too long to be one shift: almost certainly a missed clock-out, so
                // drop the session instead of billing the gap. Same outcome as a
                // session that never closed at all — but reported, because the hours
                // being dropped are the ones somebody has to correct.
                if (maxSessionMs !== null && clockOut.getTime() - open.clockIn.getTime() > maxSessionMs) {
                    options.onOverCap?.({
                        clockIn: open.clockIn,
                        clockOut,
                        hours: (clockOut.getTime() - open.clockIn.getTime()) / MS_PER_HOUR,
                    });
                    open = null;
                    break;
                }
                sessions.push({
                    clockIn: open.clockIn,
                    clockOut,
                    breaks: open.breaks,
                });
                open = null;
                break;
            }
            case 'break_start': {
                if (!open)
                    break;
                open.breaks.push({ start: new Date(event.at), end: null });
                break;
            }
            case 'break_end': {
                if (!open)
                    break;
                const last = open.breaks.at(-1);
                if (last && !last.end) {
                    last.end = new Date(event.at);
                }
                break;
            }
        }
    }
    return sessions;
}
/** Sum completed sessions into raw milliseconds. Breaks with no end count as 0. */
export function sumWorkSessions(sessions) {
    let workMs = 0;
    let breakMs = 0;
    for (const session of sessions) {
        workMs += session.clockOut.getTime() - session.clockIn.getTime();
        for (const period of session.breaks) {
            if (period.end) {
                breakMs += period.end.getTime() - period.start.getTime();
            }
        }
    }
    return { workMs, breakMs };
}
/**
 * Pair and total in one step — the entry point most callers want.
 *
 * Returns unrounded hours; callers round at their own boundary because they
 * disagree about when rounding happens relative to overtime splitting.
 */
export function calculateWorkedHours(events, options = {}) {
    const { workMs, breakMs } = sumWorkSessions(pairWorkSessions(events, options));
    const totalHours = workMs / MS_PER_HOUR;
    const breakHours = breakMs / MS_PER_HOUR;
    return { totalHours, breakHours, netHours: totalHours - breakHours };
}
